/**
 * Sticky Bar Component
 * - Shows/hides based on scroll position
 * - Updates image, variant, stock, delivery info
 * - Syncs with main product form
 * - Smooth scroll to product form on CTA click
 */

class StickyBar {
  constructor(element) {
    this.element = element;
    this.sectionId = element.dataset.sectionId;
    this.productForm = document.querySelector(`#product-form-${this.sectionId}`);
    this.variantRadios = document.querySelector('w-variant-radios');

    // Elements
    this.imageEl = element.querySelector('[data-sticky-image] img');
    this.imageMobileEl = element.querySelector('[data-sticky-image-mobile] img');
    this.variantEl = element.querySelector('[data-sticky-variant]');
    this.stockEl = element.querySelector('[data-sticky-stock]');
    this.stockTextEl = element.querySelector('[data-stock-text]');
    this.deliveryEls = element.querySelectorAll('[data-sticky-delivery]'); // Changed to querySelectorAll
    this.priceEl = element.querySelector('[data-sticky-price]');
    this.addToCartBtn = element.querySelector('[data-sticky-add-to-cart]');
    this.scrollBtns = element.querySelectorAll('[data-scroll-to-form]');
    this.swatches = element.querySelectorAll('.sticky-bar__swatch');

    this.init();
  }

  init() {
    this.setupScrollTrigger();
    this.setupScrollButtons();
    this.setupAddToCart();
    this.setupVariantSync();
    this.setupSwatchClicks();
    this.updateDeliveryDates();
  }

  /**
   * Show/hide sticky bar based on scroll position
   */
  setupScrollTrigger() {
    if (!this.productForm) return;

    this.formIsVisible = true;
    this.footerIsVisible = false;

    const formObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.formIsVisible = entry.isIntersecting;
          this.updateStickyVisibility();
        });
      },
      {
        threshold: 0,
        rootMargin: '-100px 0px 0px 0px'
      }
    );

    formObserver.observe(this.productForm);

    const footer = document.querySelector('footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.footerIsVisible = entry.isIntersecting;
            this.updateStickyVisibility();
          });
        },
        { threshold: 0 }
      );

      footerObserver.observe(footer);
    }
  }

  updateStickyVisibility() {
    if (this.formIsVisible || this.footerIsVisible) {
      this.element.classList.remove('is-visible');
    } else {
      this.element.classList.add('is-visible');
    }
  }

  /**
   * Scroll to product form smoothly
   */
  setupScrollButtons() {
    this.scrollBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        if (this.productForm) {
          const headerHeight = document.querySelector('header')?.offsetHeight || 0;
          const targetPosition = this.productForm.getBoundingClientRect().top + window.scrollY - headerHeight - 20;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      });
    });
  }

  /**
   * Add to cart from sticky bar
   */
  setupAddToCart() {
    if (!this.addToCartBtn || !this.productForm) return;

    this.addToCartBtn.addEventListener('click', (e) => {
      e.preventDefault();

      // Trigger form submit event
      const submitEvent = new Event('submit', {
        bubbles: true,
        cancelable: true
      });

      this.productForm.dispatchEvent(submitEvent);
    });
  }

  /**
   * Setup swatch click handlers
   */
  setupSwatchClicks() {
    this.swatches.forEach((swatch) => {
      swatch.addEventListener('click', () => {
        const variantValue = swatch.dataset.variantValue;

        // Find and click the corresponding radio button in the main form
        if (this.variantRadios) {
          const radioButton = this.variantRadios.querySelector(`input[type="radio"][value="${variantValue}"]`);
          if (radioButton) {
            radioButton.checked = true;
            radioButton.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        // Update active state
        this.swatches.forEach(s => s.classList.remove('is-active'));
        swatch.classList.add('is-active');
      });
    });
  }

  /**
   * Sync sticky bar with variant changes
   */
  setupVariantSync() {
    if (!this.variantRadios) return;

    // Listen to variant changes from the main form
    this.variantRadios.addEventListener('change', () => {
      const currentVariant = this.variantRadios.currentVariant;
      if (currentVariant) {
        this.updateVariant(currentVariant);
      }
    });

    // Initial update
    const currentVariant = this.variantRadios.currentVariant;
    if (currentVariant) {
      this.updateVariant(currentVariant);
    }
  }

  /**
   * Update sticky bar with new variant data
   */
  updateVariant(variant) {
    // Update desktop image
    if (this.imageEl && variant.featured_image) {
      this.imageEl.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_152x.$1');
      this.imageEl.alt = variant.featured_image.alt || variant.name;
    }

    // Update mobile image
    if (this.imageMobileEl && variant.featured_image) {
      this.imageMobileEl.src = variant.featured_image.src.replace(/\.(jpg|jpeg|png|gif|webp)/i, '_140x.$1');
      this.imageMobileEl.alt = variant.featured_image.alt || variant.name;
    }

    // Update variant title
    if (this.variantEl) {
      if (variant.title !== 'Default Title') {
        this.variantEl.textContent = variant.title.toUpperCase();
      } else {
        this.variantEl.textContent = '';
      }
    }

    // Update stock status
    if (this.stockEl && this.stockTextEl) {
      const inStockText = this.stockEl.dataset.inStockText || 'En stock';
      const outOfStockText = this.stockEl.dataset.outOfStockText || 'Rupture de stock';

      if (variant.available) {
        this.stockEl.removeAttribute('data-out-of-stock');
        this.stockTextEl.textContent = inStockText;
      } else {
        this.stockEl.setAttribute('data-out-of-stock', '');
        this.stockTextEl.textContent = outOfStockText;
      }
    }

    // Update price
    if (this.priceEl) {
      this.priceEl.dataset.basePrice = variant.price;

      let compareHtml = '';
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareHtml = `<s class="sticky-bar__compare-price" data-sticky-compare>${this.formatMoney(variant.compare_at_price)}</s> `;
      }

      // Get upsell total from main form
      const mainPriceEl = document.querySelector('[data-total-price]');
      const priceText = mainPriceEl ? mainPriceEl.textContent : this.formatMoney(variant.price);
      this.priceEl.innerHTML = `${compareHtml}<span data-sticky-current-price>${priceText}</span>`;
    }

    // Update button state
    if (this.addToCartBtn) {
      this.addToCartBtn.disabled = !variant.available;
    }
  }

  /**
   * Calculate and update delivery dates
   */
  updateDeliveryDates() {
    if (!this.deliveryEls || this.deliveryEls.length === 0) return;

    // Get settings from first element
    const firstEl = this.deliveryEls[0];
    const delay = parseInt(firstEl.dataset.delay) || 3;
    const range = parseInt(firstEl.dataset.range) || 2;
    const template = firstEl.dataset.textTemplate || 'Livraison estimée entre le {start} et le {end}';

    const today = new Date();
    const startDate = this.addBusinessDays(today, delay);
    const endDate = this.addBusinessDays(today, delay + range);

    const startFormatted = this.formatDate(startDate);
    const endFormatted = this.formatDate(endDate);

    const deliveryText = template
      .replace('{start}', `<strong>${startFormatted}</strong>`)
      .replace('{end}', `<strong>${endFormatted}</strong>`);

    // Update ALL delivery elements (desktop + mobile)
    this.deliveryEls.forEach(el => {
      el.innerHTML = deliveryText;
    });
  }

  /**
   * Add business days to a date (skip weekends)
   */
  addBusinessDays(date, days) {
    const result = new Date(date);
    let addedDays = 0;

    while (addedDays < days) {
      result.setDate(result.getDate() + 1);
      // Skip Saturday (6) and Sunday (0)
      if (result.getDay() !== 0 && result.getDay() !== 6) {
        addedDays++;
      }
    }

    return result;
  }

  /**
   * Format date as DD/MM
   */
  formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${month}`;
  }

  /**
   * Format price in money format
   */
  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, theme.moneyFormat || '{{amount}}');
    }
    // Fallback
    const amount = cents / 100;
    return `${amount.toFixed(0)}€`;
  }
}

// Initialize sticky bar when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const stickyBarElement = document.querySelector('[data-sticky-bar]');
  if (stickyBarElement) {
    new StickyBar(stickyBarElement);
  }
});
